import * as THREE from 'three';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {AnimationHandler} from 'components/customer/Preview3D/animation/entity/AnimationHandler';
import {toggleOverlayHingeDoorHandler} from 'components/customer/Preview3D/animation/overlayHingeDoorAnimationHandler';

export interface ReturnDoorFormValues {
    cabinet_width?: number;
    cabinet_return_panel_width?: number;
    cabinet_door_left?: number;
    cabinet_door_right?: number;
}

/**
 * Per-context hinge alignment applied to the door mesh BEFORE the animation
 * takes over `position.x`.
 *
 *   - 'hinge': the transform used by both animation dispatchers (RoomPlanner
 *               `handler.ts` and 3D Preview `useDoorAndDrawerAnimation.ts`).
 *   - 'none':  reserved for callers that already account for the return panel
 *               in their own build-time transform. Vinyl / shaker doors go
 *               through `calculateDoorXPositionDefault`, which offsets for
 *               `returnPanelWidth` when the door is set up; re-applying the
 *               dispatcher fixup would double-shift the panel.
 */
export type ReturnDoorHingeFixup = 'hinge' | 'none';

const defaultGetMeshSize = (mesh: THREE.Object3D): THREE.Vector3 => {
    const bbox = new THREE.Box3().setFromObject(mesh);
    const size = new THREE.Vector3();
    bbox.getSize(size);
    return size;
};

// Decide whether the given door (identified by which side it hangs on) is the
// return door of a return-product cabinet
export const isReturnDoorFor = ({
    variables,
    isRight,
}: {
    variables: TemplateVariable | undefined;
    isRight: boolean;
}): boolean => {
    if (!variables?.isReturnProduct) return false;
    if (!!variables?.isLeftReturn === isRight) return false;
    return true;
};

// X-coordinate of the return-door hinge axis, in the cabinet's local space.
export const computeReturnDoorAxis = ({
    values,
    isRight,
    carcaseThickness,
}: {
    values: ReturnDoorFormValues | undefined;
    isRight: boolean;
    carcaseThickness: number;
}): number => {
    const cabinetWidth = Number(values?.cabinet_width ?? 0);
    const returnPanelWidth = Number(values?.cabinet_return_panel_width ?? 0);
    const doorRightMargin = Number(values?.cabinet_door_right ?? 0);
    const doorLeftMargin = Number(values?.cabinet_door_left ?? 0);
    return isRight
        ? cabinetWidth - returnPanelWidth + carcaseThickness - doorRightMargin
        : returnPanelWidth + doorLeftMargin;
};

// Position of door pivoting axis varies by cabinet/door type. This issue is
// temporarily fixed by the transform below, subject to future change of mesh
// generation of cabinet panels.
const applyHingeFixup = ({
    variant,
    mesh,
    size,
    isRight,
    isPairDoor,
}: {
    variant: ReturnDoorHingeFixup;
    mesh: THREE.Object3D;
    size: THREE.Vector3;
    isRight: boolean;
    isPairDoor: boolean;
}): void => {
    // The guard mirrors the dispatcher convention: for a plain left-hang door
    // (`!isRight`) and for a pair-right door (`isPairDoor && isRight`) we mirror
    // the mesh on X and shift by its width. Non-pair right doors are already
    // positioned at the hinge axis by the mesh builder, so we skip the fixup.
    if (variant === 'none') return;
    if (!(isPairDoor || !isRight)) return;
    mesh.scale.x = -1;
    mesh.position.x += (isRight ? +1 : -1) * size.x;
};

// Compose a return-door animation handler for the given mesh, or return `null`
// when the caller's door is not the return door of a return-product cabinet.
// Returning `null` is a signal for callers to fall through to their normal
// (non-return) animation path.
//
// Note: Three sites share this helper:
//   - `Preview3DCommon/animations/handler.ts` (RoomPlanner): variant 'hinge'.
//   - `Preview3D/animation/useDoorAndDrawerAnimation.ts` (ProductVisualizer): variant 'hinge'.
//   - `Preview3DCommon/3d-models/shaker.ts` (Vinyl / shaker doors): variant 'none',
//     because the shaker's own build-time `calculateDoorXPositionDefault`
//     already offsets for `returnPanelWidth`; adding the dispatcher's pre-
//     rotation flip/translate on top would double-shift the panel. Shaker doors
//     additionally pass `axisPosition` (their build-time `position.x`) because
//     the shaker group origin already sits on the hinge edge, in whichever
//     frame the caller built it.
export const createReturnDoorAnimationHandler = ({
    mesh,
    variables,
    values,
    isRight,
    isPairDoor,
    carcaseThickness,
    hingeFixup,
    axisPosition: axisPositionOverride,
    getMeshSize = defaultGetMeshSize,
}: {
    mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh;
    variables: TemplateVariable | undefined;
    values: ReturnDoorFormValues | undefined;
    isRight: boolean;
    isPairDoor: boolean;
    carcaseThickness: number;
    hingeFixup: ReturnDoorHingeFixup;
    // Hinge axis in the mesh's own frame. When omitted it is derived from the
    // form values via `computeReturnDoorAxis` (absolute, left-edge frame).
    axisPosition?: number;
    getMeshSize?: (m: THREE.Object3D) => THREE.Vector3;
}): AnimationHandler | null => {
    if (!isReturnDoorFor({variables, isRight})) return null;

    const axisPosition =
        axisPositionOverride ??
        computeReturnDoorAxis({
            values,
            isRight,
            carcaseThickness,
        });

    applyHingeFixup({
        variant: hingeFixup,
        mesh,
        size: getMeshSize(mesh),
        isRight,
        isPairDoor,
    });

    return toggleOverlayHingeDoorHandler({
        mesh,
        axisPosition,
        isRightDoor: isRight,
        doorStopOffsetX: 30,
    });
};
